/**
 * CanalJornadaDetalhe V55 — Drill-down de um cliente com timeline, KPIs e risco
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { StageBadge } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ArrowLeft, Users, FileCheck, ClipboardCheck, Heart } from "lucide-react";
import { useParams, Link } from "wouter";
import { GaugeChart } from "@/components/home";

export default function CanalJornadaDetalhe() {
  usePageTitle("Detalhe do Cliente");
  const { user } = useAuth();
  const params = useParams<{ id: string }>();
  const companyId = Number(params.id);

  const { data, isLoading, error } = trpc.dashboardsAdv.channel.journeyDetail.useQuery(
    { companyId },
    { enabled: !!user && !isNaN(companyId), refetchOnWindowFocus: false }
  );

  if (error) {
    return (
      <SalviaLayout title="Detalhe do Cliente">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title={`Detalhe — ${data?.company?.nomeFantasia || "Carregando..."}`}>
      <div className="space-y-6">
        <Link href="/dashboards/canal/jornada" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> Voltar para Jornada
        </Link>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
        ) : (
          <>
            <Card className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#002F59]">{data.company.nomeFantasia || data.company.razaoSocial}</h2>
                  <p className="text-sm text-muted-foreground mt-1">CNPJ: {data.company.cnpj} | {data.company.cidade}, {data.company.estado}</p>
                </div>
                <StageBadge stage={data.stage} />
              </div>
            </Card>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: Users, label: "Vidas", value: data.kpis.totalVidas.toLocaleString("pt-BR"), color: "text-[#002F59]" },
                { icon: ClipboardCheck, label: "Respostas", value: data.kpis.totalSubmissions, color: "text-[#002F59]" },
                { label: "Concluídas", value: data.kpis.concluidas, color: "text-emerald-600" },
                { label: "Bloqueadas", value: data.kpis.bloqueadas, color: "text-red-600" },
                { icon: FileCheck, label: "Documentos", value: data.kpis.docsReady, color: "text-emerald-600" },
                { icon: Heart, label: "Saúde", value: data.kpis.saude, color: data.kpis.saude >= 70 ? "text-emerald-600" : data.kpis.saude >= 40 ? "text-amber-600" : "text-red-600" },
              ].map((kpi, i) => (
                <Card key={i} className="p-4 text-center">
                  {kpi.icon && <kpi.icon size={20} className={`mx-auto ${kpi.color} mb-1`} />}
                  <div className={`text-2xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</div>
                  <div className="text-xs text-muted-foreground">{kpi.label}</div>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Card className="p-6 flex flex-col items-center"><GaugeChart value={data.kpis.adesao} label="Adesão" subtitle="%" /></Card>
              <Card className="p-6 flex flex-col items-center"><GaugeChart value={data.kpis.progresso} label="Progresso" subtitle="%" /></Card>
              <Card className="p-6 flex flex-col items-center"><GaugeChart value={data.kpis.saude} label="Saúde da Conta" /></Card>
            </div>

            {data.riskDistribution && data.riskDistribution.length > 0 && (
              <Card className="p-6">
                <h3 className="text-sm font-semibold text-[#002F59] mb-4">Distribuição de Risco</h3>
                <div className="flex gap-4 flex-wrap">
                  {data.riskDistribution.map((r: any, i: number) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: r.color }} />
                      <span className="text-sm">{r.label}: <strong>{r.count}</strong></span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h3 className="text-sm font-semibold text-[#002F59] mb-4">Timeline da Jornada</h3>
              {data.timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum evento registrado</p>
              ) : (
                <div className="relative pl-6 space-y-4">
                  <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-slate-200" />
                  {data.timeline.map((event: any, i: number) => (
                    <div key={i} className="relative">
                      <div className="absolute -left-3.5 top-1.5 w-3 h-3 rounded-full bg-[#002F59] border-2 border-white" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{new Date(event.date).toLocaleDateString("pt-BR")}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{event.type}</span>
                        </div>
                        <p className="text-sm font-medium mt-0.5">{event.event}</p>
                        <p className="text-xs text-muted-foreground">{event.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </SalviaLayout>
  );
}
